import React from "react";

import ListItem from "../../components/list/ListItem";

import "./List.scss";

interface ListProps<T> {
    header?: string;
    headerIcon?: string;
    clearTop?: boolean;
    clearBottom?: boolean;
    tokenized?: boolean;
    className?: string;
    multiSelect?: boolean;
    joined?: boolean;
    children?:
        | React.ReactElement<ListItem<T>>[]
        | React.ReactElement<ListItem<T>>;
    selected?: T;
    selections?: T[];
    selectionChanged?: (data: T, selections: T[]) => void;
    itemDoubleClicked?: (data: T) => void;
}

interface ListState<T> {
    selected?: T;
    selections?: T[];
}

export const ListContext = React.createContext({
    isSelected: (data: any) => false as boolean,
    setSelectedItem: (data: any) => {},
    doubleClicked: (data: any) => {},
});

class List<T> extends React.Component<ListProps<T>, ListState<T>> {
    state = {
        selectedData: null,
        selectionsData: [],
    } as ListState<T>;

    getSelectedData(): T {
        return this.props.selected ?? this.state.selected ?? null;
    }

    getSelectionsData(): T[] {
        return this.props.selections ?? this.state.selections ?? [];
    }

    render(): React.ReactNode {
        let classNames = " " + (this.props.className ?? "");

        if (this.props.clearTop) classNames += " top-clearance";
        if (this.props.clearBottom) classNames += " bottom-clearance";
        if (this.props.tokenized) classNames += " tokenized";
        if (this.props.joined) classNames += " joined";

        const contextValue = {
            isSelected: (data: T) => {
                if (this.props.multiSelect) {
                    return this.getSelectionsData().includes(data);
                } else {
                    return this.getSelectedData() === data;
                }
            },
            setSelectedItem: (data: T) => {
                const selectionsData = [...(this.state.selections ?? [])];

                let wasRemoval = false;
                if (this.props.multiSelect) {
                    if (selectionsData.includes(data)) {
                        selectionsData.remove(data);
                        wasRemoval = true;
                    } else {
                        selectionsData.push(data);
                    }

                    this.setState(() => ({
                        selected: wasRemoval ? undefined : data,
                        selections: selectionsData,
                    }));
                } else {
                    this.setState(() => ({
                        selected: wasRemoval ? undefined : data,
                        selections: [data],
                    }));
                }
                this.props.selectionChanged?.(
                    wasRemoval ? undefined : data,
                    selectionsData,
                );
            },
            doubleClicked: (data: T) => {
                this.setState(() => ({ selected: data }));
                this.props.itemDoubleClicked?.(data);
            },
        };

        return (
            <ListContext.Provider value={contextValue}>
                <div className={`items${classNames}`}>
                    {this.props.header && (
                        <span className="list-header">{this.props.header}</span>
                    )}
                    {this.props.headerIcon && (
                        <i
                            className={`list-header-icon fa-solid fa-${this.props.headerIcon}`}></i>
                    )}
                    {this.props.children}
                </div>
            </ListContext.Provider>
        );
    }
}

export default List;
